import React, { useMemo, useState } from "react";
import {
  MAX_HEADING_LENGTH,
  MAX_LEARNING_CONTENT_BLOCKS,
  MAX_IMAGE_ALT_TEXT_LENGTH,
  MAX_IMAGE_CAPTION_LENGTH,
  MAX_LINK_LABEL_LENGTH,
  MAX_LINK_URL_LENGTH,
  MAX_RESOURCE_LABEL_LENGTH,
  MAX_TEXT_LENGTH,
  MAX_VIDEO_TITLE_LENGTH,
  normalizeLearningContentDraftBlocks,
} from "./utils/learningContent";
import {
  createLearningContentMediaId,
  validateLearningContentMediaFile,
} from "./utils/learningContentMediaApi";
import "./LearningContentBuilder.css";
import LearningContentVideoPlayer from "./LearningContentVideoPlayer";

let fallbackIdSequence = 0;

export const createLearningContentBlockId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `lc-${crypto.randomUUID()}`;
  }
  fallbackIdSequence += 1;
  return `lc-${Date.now().toString(36)}-${fallbackIdSequence.toString(36)}`;
};

export const createLearningContentBlock = (type, createId = createLearningContentBlockId) => {
  const id = createId();
  if (type === "heading") return { id, type, level: 2, text: "" };
  if (type === "text") return { id, type, text: "" };
  if (type === "link") return { id, type, label: "", url: "https://" };
  if (type === "video") return { id, type, title: "", url: "" };
  if (type === "image") return { id, type, storagePath: "", altText: "", caption: "" };
  if (type === "resource") return { id, type, storagePath: "", label: "" };
  throw new Error("Unsupported learning-content block type.");
};

export const moveLearningContentBlock = (blocks, index, direction) => {
  const target = index + direction;
  if (index < 0 || index >= blocks.length || target < 0 || target >= blocks.length) {
    return blocks;
  }
  const reordered = [...blocks];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
  return reordered;
};

const blockError = (block, index, mediaDrafts) => {
  try {
    normalizeLearningContentDraftBlocks([block], mediaDrafts);
    return "";
  } catch (error) {
    const pathPrefix = `blocks[${index}]`;
    return error.path?.startsWith(pathPrefix) || error.path?.startsWith("blocks[0]")
      ? error.message.replaceAll("blocks[0]", `Block ${index + 1}`)
      : error.message;
  }
};

const LearningContentBuilder = ({
  blocks,
  onChange,
  onBack,
  onSave,
  onClear,
  saving = false,
  saveError = "",
  saveSuccess = "",
  existingContent = false,
  saveLabel = "",
  createId = createLearningContentBlockId,
  mediaDrafts = {},
  onMediaDraftsChange = () => {},
  mediaPreviews = {},
}) => {
  const [submitError, setSubmitError] = useState("");
  const errors = useMemo(
    () => blocks.map((block, index) => blockError(block, index, mediaDrafts)),
    [blocks, mediaDrafts]
  );
  const atBlockLimit = blocks.length >= MAX_LEARNING_CONTENT_BLOCKS;

  const addBlock = (type) => {
    if (atBlockLimit || saving) return;
    setSubmitError("");
    onChange([...blocks, createLearningContentBlock(type, createId)]);
  };

  const updateBlock = (index, updates) => {
    setSubmitError("");
    onChange(
      blocks.map((block, blockIndex) =>
        blockIndex === index ? { ...block, ...updates } : block
      )
    );
  };

  const removeBlock = (index) => {
    setSubmitError("");
    const removed = blocks[index];
    if (mediaDrafts[removed.id]?.previewUrl) URL.revokeObjectURL(mediaDrafts[removed.id].previewUrl);
    onMediaDraftsChange((current) => {
      const nextDrafts = { ...current };
      const uploadedMedia = nextDrafts[removed.id]?.uploadedMedia;
      delete nextDrafts[removed.id];
      if (uploadedMedia) {
        nextDrafts[`cleanup-${uploadedMedia.mediaId}`] = { uploadedMedia, status: "discarded" };
      }
      return nextDrafts;
    });
    onChange(blocks.filter((_block, blockIndex) => blockIndex !== index));
  };

  const chooseMedia = (block, file) => {
    setSubmitError("");
    const kind = block.type === "image" ? "image" : "resource";
    try {
      validateLearningContentMediaFile(file, kind);
      onMediaDraftsChange((current) => {
        const previous = current[block.id];
        if (previous?.previewUrl) URL.revokeObjectURL(previous.previewUrl);
        return {
          ...current,
          [block.id]: {
            file,
            mediaId: createLearningContentMediaId(),
            previewUrl: URL.createObjectURL(file),
            previousStoragePath: block.storagePath,
            status: "ready",
            error: "",
            uploadedMedia: null,
          },
        };
      });
    } catch (error) {
      onMediaDraftsChange((current) => ({
        ...current,
        [block.id]: { ...(current[block.id] || {}), file: null, status: "failed", error: error.message },
      }));
    }
  };

  const clearMediaDraft = (block, index) => {
    const blockId = block.id;
    if (mediaDrafts[blockId]?.previewUrl) URL.revokeObjectURL(mediaDrafts[blockId].previewUrl);
    const previousStoragePath = mediaDrafts[blockId]?.previousStoragePath || "";
    onMediaDraftsChange((current) => {
      const next = { ...current };
      const uploadedMedia = next[blockId]?.uploadedMedia;
      delete next[blockId];
      if (uploadedMedia) {
        next[`cleanup-${uploadedMedia.mediaId}`] = { uploadedMedia, status: "discarded" };
      }
      return next;
    });
    updateBlock(index, { storagePath: previousStoragePath });
  };

  const moveBlock = (index, direction) => {
    setSubmitError("");
    onChange(moveLearningContentBlock(blocks, index, direction));
  };

  const submit = async () => {
    setSubmitError("");
    if (blocks.length > 0) {
      try {
        const normalized = normalizeLearningContentDraftBlocks(blocks, mediaDrafts);
        await onSave?.(normalized);
      } catch (error) {
        setSubmitError(error.message || "Please correct the learning-content errors.");
      }
      return;
    }
    await onSave?.([]);
  };

  return (
    <section className="learning-content-builder" aria-labelledby="learning-content-title">
      <header className="learning-content-builder-header">
        <div>
          <p className="learning-content-eyebrow">Optional learning material</p>
          <h1 id="learning-content-title">Learning Content</h1>
          <p>
            Add material students can review before the assessment. This page supports
            headings, plain text, secure external links, videos, images and PDF resources.
          </p>
        </div>
        <button type="button" className="learning-content-back" onClick={onBack} disabled={saving}>
          Back to Test
        </button>
      </header>

      <div className="learning-content-add-row" aria-label="Add learning content block">
        <button type="button" onClick={() => addBlock("heading")} disabled={atBlockLimit || saving}>
          + Heading
        </button>
        <button type="button" onClick={() => addBlock("text")} disabled={atBlockLimit || saving}>
          + Text
        </button>
        <button type="button" onClick={() => addBlock("link")} disabled={atBlockLimit || saving}>
          + Link
        </button>
        <button type="button" onClick={() => addBlock("video")} disabled={atBlockLimit || saving}>
          + Video
        </button>
        <button type="button" onClick={() => addBlock("image")} disabled={atBlockLimit || saving}>
          + Image
        </button>
        <button type="button" onClick={() => addBlock("resource")} disabled={atBlockLimit || saving}>
          + PDF Resource
        </button>
        <span>{blocks.length}/{MAX_LEARNING_CONTENT_BLOCKS} blocks</span>
      </div>

      {atBlockLimit && (
        <div className="learning-content-limit" role="status">
          This learning page has reached the 50-block limit.
        </div>
      )}

      {blocks.length === 0 ? (
        <div className="learning-content-empty">
          <h2>No learning content added yet</h2>
          <p>
            Add a heading, text section, external link, video, image or PDF resource to create an optional learning
            page for this test.
          </p>
        </div>
      ) : (
        <div className="learning-content-block-list">
          {blocks.map((block, index) => (
            <article className="learning-content-block" key={block.id} data-block-id={block.id}>
              <div className="learning-content-block-heading">
                <div>
                  <span className="learning-content-block-number">{index + 1}</span>
                   <h2>{block.type === "heading" ? "Heading" : block.type === "text" ? "Text" : block.type === "link" ? "External Link" : block.type === "video" ? "Video" : block.type === "image" ? "Image" : "PDF Resource"}</h2>
                </div>
                <div className="learning-content-block-actions">
                  <button
                    type="button"
                    onClick={() => moveBlock(index, -1)}
                    disabled={index === 0 || saving}
                    aria-label={`Move block ${index + 1} up`}
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveBlock(index, 1)}
                    disabled={index === blocks.length - 1 || saving}
                    aria-label={`Move block ${index + 1} down`}
                    title="Move down"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="learning-content-delete"
                    onClick={() => removeBlock(index)}
                    disabled={saving}
                    aria-label={`Delete block ${index + 1}`}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {block.type === "heading" && (
                <div className="learning-content-field-grid heading-grid">
                  <label>
                    Heading text
                    <input
                      type="text"
                      value={block.text}
                      onChange={(event) => updateBlock(index, { text: event.target.value })}
                      maxLength={MAX_HEADING_LENGTH + 1}
                      disabled={saving}
                    />
                    <span>{block.text.length}/{MAX_HEADING_LENGTH}</span>
                  </label>
                  <label>
                    Heading level
                    <select
                      value={block.level}
                      onChange={(event) => updateBlock(index, { level: Number(event.target.value) })}
                      disabled={saving}
                    >
                      <option value="2">Heading 2</option>
                      <option value="3">Heading 3</option>
                    </select>
                  </label>
                </div>
              )}

              {block.type === "text" && (
                <label className="learning-content-text-field">
                  Text
                  <textarea
                    rows="8"
                    value={block.text}
                    onChange={(event) => updateBlock(index, { text: event.target.value })}
                    maxLength={MAX_TEXT_LENGTH + 1}
                    disabled={saving}
                    placeholder="Write one or more plain-text paragraphs"
                  />
                  <span>{block.text.length}/{MAX_TEXT_LENGTH}</span>
                </label>
              )}

              {block.type === "link" && (
                <div className="learning-content-field-grid">
                  <label>
                    Link label
                    <input
                      type="text"
                      value={block.label}
                      onChange={(event) => updateBlock(index, { label: event.target.value })}
                      maxLength={MAX_LINK_LABEL_LENGTH + 1}
                      disabled={saving}
                    />
                    <span>{block.label.length}/{MAX_LINK_LABEL_LENGTH}</span>
                  </label>
                  <label>
                    HTTPS URL
                    <input
                      type="url"
                      value={block.url}
                      onChange={(event) => updateBlock(index, { url: event.target.value })}
                      maxLength={MAX_LINK_URL_LENGTH + 1}
                      disabled={saving}
                      placeholder="https://example.com/resource"
                    />
                    <span>{block.url.length}/{MAX_LINK_URL_LENGTH}</span>
                  </label>
                </div>
              )}

              {block.type === "video" && (
                <div className="learning-content-video-editor">
                  <div className="learning-content-field-grid">
                    <label>
                      Video title (optional)
                      <input type="text" value={block.title} maxLength={MAX_VIDEO_TITLE_LENGTH + 1} disabled={saving}
                        onChange={(event) => updateBlock(index, { title: event.target.value })} />
                      <span>{block.title.length}/{MAX_VIDEO_TITLE_LENGTH}</span>
                    </label>
                    <label>
                      Video URL
                      <input type="url" value={block.url} maxLength={MAX_LINK_URL_LENGTH + 1} disabled={saving}
                        placeholder="https://www.youtube.com/watch?v=... or https://example.com/video.mp4"
                        onChange={(event) => updateBlock(index, { url: event.target.value })} />
                      <span>{block.url.length}/{MAX_LINK_URL_LENGTH}</span>
                    </label>
                  </div>
                  <p className="learning-content-video-help">Use a YouTube watch, youtu.be or embed link, or a public HTTPS video file. Playback depends on the video host and browser.</p>
                  {!errors[index] && (
                    <div className="learning-content-video-preview">
                      <p>Video preview</p>
                      <LearningContentVideoPlayer block={block} preview />
                    </div>
                  )}
                </div>
              )}

              {block.type === "image" && (
                <div className="learning-content-media-editor">
                  {(mediaDrafts[block.id]?.previewUrl || mediaPreviews[block.id]?.url) && (
                    <img
                      className="learning-content-image-preview"
                      src={mediaDrafts[block.id]?.previewUrl || mediaPreviews[block.id]?.url}
                      alt={block.altText || "Selected learning-content preview"}
                    />
                  )}
                  <div className="learning-content-media-controls">
                    <label>
                      {block.storagePath || mediaDrafts[block.id]?.file ? "Replace image" : "Select image"}
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={saving}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) chooseMedia(block, file);
                          event.target.value = "";
                        }}
                      />
                    </label>
                    {(mediaDrafts[block.id]?.file || mediaDrafts[block.id]?.uploadedMedia) && (
                      <button type="button" className="learning-content-clear-file" onClick={() => clearMediaDraft(block, index)} disabled={saving}>
                        Clear replacement
                      </button>
                    )}
                    <p className={`learning-content-upload-status ${mediaDrafts[block.id]?.status || mediaPreviews[block.id]?.status || "saved"}`}>
                      {mediaDrafts[block.id]?.fileName || mediaDrafts[block.id]?.file?.name || (block.storagePath ? block.storagePath.split("/").pop() : "No image selected")}
                      {" · "}{mediaDrafts[block.id]?.status === "uploading" ? "Uploading" : mediaDrafts[block.id]?.status === "uploaded" ? "Uploaded" : mediaDrafts[block.id]?.status === "failed" ? "Failed" : mediaDrafts[block.id]?.file ? "Ready" : block.storagePath ? "Saved" : "Required"}
                    </p>
                    <div className="learning-content-field-grid">
                      <label>
                        Alt text
                        <input type="text" value={block.altText} maxLength={MAX_IMAGE_ALT_TEXT_LENGTH + 1} disabled={saving} onChange={(event) => updateBlock(index, { altText: event.target.value })} />
                        <span>{block.altText.length}/{MAX_IMAGE_ALT_TEXT_LENGTH}</span>
                      </label>
                      <label>
                        Caption (optional)
                        <input type="text" value={block.caption} maxLength={MAX_IMAGE_CAPTION_LENGTH + 1} disabled={saving} onChange={(event) => updateBlock(index, { caption: event.target.value })} />
                        <span>{block.caption.length}/{MAX_IMAGE_CAPTION_LENGTH}</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {block.type === "resource" && (
                <div className="learning-content-media-editor learning-content-resource-editor">
                  <div className="learning-content-media-controls">
                    <label>
                      {block.storagePath || mediaDrafts[block.id]?.file ? "Replace PDF" : "Select PDF"}
                      <input
                        type="file"
                        accept="application/pdf"
                        disabled={saving}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) chooseMedia(block, file);
                          event.target.value = "";
                        }}
                      />
                    </label>
                    {(mediaDrafts[block.id]?.previewUrl || mediaPreviews[block.id]?.url) && (
                      <a className="learning-content-resource-preview" href={mediaDrafts[block.id]?.previewUrl || mediaPreviews[block.id]?.url} target="_blank" rel="noreferrer">Preview PDF</a>
                    )}
                    {(mediaDrafts[block.id]?.file || mediaDrafts[block.id]?.uploadedMedia) && (
                      <button type="button" className="learning-content-clear-file" onClick={() => clearMediaDraft(block, index)} disabled={saving}>
                        Clear replacement
                      </button>
                    )}
                    <p className={`learning-content-upload-status ${mediaDrafts[block.id]?.status || mediaPreviews[block.id]?.status || "saved"}`}>
                      {mediaDrafts[block.id]?.file?.name || (block.storagePath ? block.storagePath.split("/").pop() : "No PDF selected")}
                      {" · "}{mediaDrafts[block.id]?.status === "uploading" ? "Uploading" : mediaDrafts[block.id]?.status === "uploaded" ? "Uploaded" : mediaDrafts[block.id]?.status === "failed" ? "Failed" : mediaDrafts[block.id]?.file ? "Ready" : block.storagePath ? "Saved" : "Required"}
                    </p>
                    <label>
                      Resource label
                      <input type="text" value={block.label} maxLength={MAX_RESOURCE_LABEL_LENGTH + 1} disabled={saving} onChange={(event) => updateBlock(index, { label: event.target.value })} />
                      <span>{block.label.length}/{MAX_RESOURCE_LABEL_LENGTH}</span>
                    </label>
                  </div>
                </div>
              )}

              {mediaDrafts[block.id]?.error && (
                <div className="learning-content-inline-error" role="alert">{mediaDrafts[block.id].error}</div>
              )}
              {!mediaDrafts[block.id]?.error && mediaPreviews[block.id]?.error && (
                <div className="learning-content-inline-error" role="alert">{mediaPreviews[block.id].error}</div>
              )}

              {errors[index] && (
                <div className="learning-content-inline-error" role="alert">
                  {errors[index]}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="learning-content-message-region" aria-live="polite">
        {(submitError || saveError) && (
          <div className="learning-content-save-error">{submitError || saveError}</div>
        )}
        {saveSuccess && <div className="learning-content-save-success">{saveSuccess}</div>}
      </div>

      <footer className="learning-content-footer">
        <button type="button" className="learning-content-secondary" onClick={onBack} disabled={saving}>
          Back to Test
        </button>
        {existingContent && onClear && (
          <button type="button" className="learning-content-remove-page" onClick={onClear} disabled={saving}>
            Remove Learning Content
          </button>
        )}
        <button type="button" className="learning-content-primary" onClick={submit} disabled={saving}>
          {saving ? "Saving…" : saveLabel || (existingContent ? "Save Learning Content" : "Use This Content")}
        </button>
      </footer>
    </section>
  );
};

export default LearningContentBuilder;
